import { Button } from "@shared/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PageHeader } from "@shared/components/PageHeader";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ArrowLeft,
  Eye,
  Upload,
  File as FileIcon,
  X,
  FileText,
  CheckCircle2,
  Copy,
  AlertCircle,
} from "lucide-react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useRef, useState, useEffect } from "react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";
import { GenericTable } from "@shared/components/GenericTable";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { TableColumn } from "@shared/types/table";
import React from "react";
import {
  useUploadBulkPaymentBatch,
  useBulkPaymentBatchPreview,
  useSaveBulkPaymentBatchMappings,
  useDownloadBulkPaymentTemplate,
} from "../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

const FILE_FORMAT_OPTIONS = [
  { label: "Generic CSV (auto-detect)", value: "Generic CSV (auto-detect)" },
  { label: "Custom CSV", value: "Custom CSV" },
  { label: "Standard Excel", value: "Standard Excel" },
  { label: "Standard XML", value: "Standard XML" },
  { label: "Standard JSON", value: "Standard JSON" },
  { label: "NACHA ACH", value: "NACHA ACH" },
  { label: "BAI2", value: "BAI2" },
  { label: "MT940 SWIFT", value: "MT940 SWIFT" },
  { label: "Fedwire", value: "Fedwire" },
  { label: "BTRS", value: "BTRS" },
  { label: "Standard Lockbox", value: "Standard Lockbox" },
  { label: "Wells Fargo Lockbox", value: "Wells Fargo Lockbox" },
  { label: "JPM Lockbox", value: "JPM Lockbox" },
  { label: "EDI 820", value: "EDI 820" },
  { label: "EDI 823", value: "EDI 823" },
  { label: "Invoice Cloud", value: "Invoice Cloud" },
  { label: "Paymentus", value: "Paymentus" },
  { label: "KUBRA", value: "KUBRA" },
  { label: "Speedpay", value: "Speedpay" },
  { label: "Stripe", value: "Stripe" },
  { label: "PayPal", value: "PayPal" },
  { label: "Braintree", value: "Braintree" },
  { label: "Square", value: "Square" },
  { label: "Authorize.net", value: "Authorize.net" },
  { label: "Visa TPS", value: "Visa TPS" },
  { label: "Mastercard clearing", value: "Mastercard clearing" },
  { label: "Zelle", value: "Zelle" },
  { label: "Venmo", value: "Venmo" },
  { label: "NCR", value: "NCR" },
  { label: "Clover", value: "Clover" },
  { label: "Square POS", value: "Square POS" },
  { label: "CityBase", value: "CityBase" },
  { label: "DivDat", value: "DivDat" },
  { label: "LIHEAP", value: "LIHEAP" },
  { label: "Local Gov Assistance", value: "Local Gov Assistance" },
  { label: "Internal Adjustment CSV", value: "Internal Adjustment CSV" },
];

const PAYMENT_SOURCE_MAP: Record<string, string> = {
  csv: "Bank (ACH / Wire / SWIFT)",
  csv_custom: "Bank (ACH / Wire / SWIFT)",
  excel: "Bank (ACH / Wire / SWIFT)",
  xml: "Bank (ACH / Wire / SWIFT)",
  json: "Bank (ACH / Wire / SWIFT)",
  nacha: "Bank (ACH / Wire / SWIFT)",
  bai2: "Bank (ACH / Wire / SWIFT)",
  mt940: "Bank (ACH / Wire / SWIFT)",
  fedwire: "Bank (ACH / Wire / SWIFT)",
  btrs: "Bank (ACH / Wire / SWIFT)",
  lockbox: "Bank (ACH / Wire / SWIFT)",
  lockbox_wf: "Bank (ACH / Wire / SWIFT)",
  lockbox_jpm: "Bank (ACH / Wire / SWIFT)",
  edi820: "Bank (ACH / Wire / SWIFT)",
  edi823: "Bank (ACH / Wire / SWIFT)",
  invoice_cloud: "Payment Vendor",
  paymentus: "Payment Vendor",
  kubra: "Payment Vendor",
  speedpay: "Payment Vendor",
  stripe: "Payment Gateway",
  paypal: "Payment Gateway",
  braintree: "Payment Gateway",
  square: "Payment Gateway",
  authorize_net: "Payment Gateway",
  visa_tps: "Payment Gateway",
  mastercard: "Payment Gateway",
  zelle: "Online Portal / Mobile App",
  venmo: "Online Portal / Mobile App",
  ncr: "POS / Counter",
  clover: "POS / Counter",
  square_pos: "POS / Counter",
  citybase: "Kiosk",
  divdat: "Kiosk",
  liheap: "Government / Agency",
  local_gov: "Government / Agency",
  internal: "Internal Adjustment",
};

const PAYMENT_SOURCE_OPTIONS = [
  { label: "Bank (ACH / Wire / SWIFT)", value: "Bank (ACH / Wire / SWIFT)" },
  { label: "Lockbox", value: "Lockbox" },
  { label: "Payment Gateway", value: "Payment Gateway" },
  { label: "Payment Vendor", value: "Payment Vendor" },
  { label: "POS / Counter", value: "POS / Counter" },
  { label: "Kiosk", value: "Kiosk" },
  { label: "Online Portal / Mobile App", value: "Online Portal / Mobile App" },
  { label: "Government / Agency", value: "Government / Agency" },
  { label: "Internal Adjustment", value: "Internal Adjustment" },
];

const BulkPaymentCreate = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>(
    {},
  );
  const [batchId, setBatchId] = useState<number | null>(null);

  const remoteUtilityId = useRemoteUtilityId();
  const uploadMutation = useUploadBulkPaymentBatch();
  const saveMappingsMutation = useSaveBulkPaymentBatchMappings(batchId);
  const downloadTemplate = useDownloadBulkPaymentTemplate();
  const { data: previewData, isLoading: isPreviewLoading } =
    useBulkPaymentBatchPreview(batchId);

  const mappingData = React.useMemo(() => {
    if (!previewData) return [];
    const required = (previewData.requiredFields || []).map((f: any) => ({
      id: f.field,
      field: f.label,
      desc: f.description,
      req: f.required,
    }));
    const optional = (previewData.optionalFields || []).map((f: any) => ({
      id: f.field,
      field: f.label,
      desc: f.description,
      req: f.required,
    }));
    return [...required, ...optional];
  }, [previewData]);

  const fileColumns = React.useMemo(() => {
    if (!previewData) return [];
    return (previewData.detectedColumns || []).map((col: string) => ({
      key: col,
      label: col,
    }));
  }, [previewData]);

  const previewRows = React.useMemo(() => {
    if (!previewData) return [];
    return (previewData.previewRows || []).map((row: any, idx: number) => ({
      id: idx,
      ...row,
    }));
  }, [previewData]);

  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [formatHelperText, setFormatHelperText] = useState(
    "Generic CSV detects columns automatically. Custom templates use the column mapping below.",
  );

  const getAcceptedExtensions = (format: string) => {
    switch (format) {
      case "Generic CSV (auto-detect)":
      case "Custom CSV":
      case "Internal Adjustment CSV":
        return ".csv,.txt";
      case "Standard Excel":
        return ".xlsx,.xls";
      case "Standard XML":
        return ".xml";
      case "Standard JSON":
        return ".json";
      case "NACHA ACH":
        return ".ach,.txt";
      case "BAI2":
        return ".bai,.bai2,.txt";
      case "MT940 SWIFT":
        return ".mt940,.txt";
      case "EDI 820":
      case "EDI 823":
        return ".edi,.txt";
      case "Standard Lockbox":
      case "Wells Fargo Lockbox":
      case "JPM Lockbox":
        return ".dat,.txt,.csv";
      default:
        return ".csv,.txt,.xlsx,.xls,.xml,.json,.edi,.ach,.ica,.bai,.bai2,.dat";
    }
  };

  useEffect(() => {
    if (selectedFile && selectedFormat) {
      const validExtensions = getAcceptedExtensions(selectedFormat).split(",");
      const fileExtension = selectedFile.name
        .substring(selectedFile.name.lastIndexOf("."))
        .toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setSelectedFile(null);
        toast({
          title: "Format mismatch",
          description: `The file was removed because it doesn't match the valid extensions (${validExtensions.join(", ")}) for the newly selected format.`,
          variant: "destructive",
        });
      }
    }
  }, [selectedFormat, selectedFile]);

  const fields: FormField[] = [
    {
      name: "fileFormat",
      label: "File Format",
      type: "select",
      required: true,
      options: FILE_FORMAT_OPTIONS,
      fullWidth: true,
      helperText: formatHelperText,
      className: "col-span-2",
    },
    {
      name: "paymentSource",
      label: "Payment Source",
      type: "select",
      required: true,
      options: PAYMENT_SOURCE_OPTIONS,
      className: "col-span-1",
    },
    {
      name: "duplicateDetection",
      label: "Duplicate Detection",
      type: "select",
      required: false,
      options: [
        { label: "By Transaction ID (recommended)", value: "transaction_id" },
        {
          label: "By transaction hash (txn + amount + date)",
          value: "transaction_id_amount_date",
        },
        { label: "By reference / check number", value: "reference" },
        { label: "Disabled", value: "disabled" },
      ],
      className: "col-span-1",
    },
  ];

  const form = FormService.createForm({
    fields: fields,
    mode: "onChange",
    defaultValues: {
      fileFormat: "csv",
      paymentSource: "bank",
      duplicateDetection: "transaction_id",
    },
  });

  const handleFieldChange = (name: string, value: any) => {
    if (name === "fileFormat") {
      setSelectedFormat(value);
      const sourceKey = PAYMENT_SOURCE_MAP[value] || "bank";
      form.setValue("paymentSource", sourceKey);

      if (value === "Generic CSV (auto-detect)") {
        setFormatHelperText(
          "Generic CSV detects columns automatically. Custom templates use the column mapping below.",
        );
      } else if (sourceKey === "Payment Gateway") {
        setFormatHelperText(
          "Select a gateway format to automatically map gateway-specific fields and metadata.",
        );
      } else if (sourceKey === "Payment Vendor") {
        setFormatHelperText(
          "Vendor formats are strictly validated against vendor specifications.",
        );
      } else if (sourceKey === "Kiosk") {
        setFormatHelperText(
          "Kiosk format expects location identifiers and hardware terminal IDs.",
        );
      } else {
        setFormatHelperText(
          "Pre-configured format template for standardized processing.",
        );
      }
    }
  };

  const handleFileSelect = (file: File) => {
    const validExtensions = getAcceptedExtensions(selectedFormat).split(",");
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast({
        title: "Invalid file type",
        description: `Please upload a supported file format (${validExtensions.join(", ")}).`,
        variant: "destructive",
      });
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast({
        title: "File exceeds limit",
        description: "File exceeds 20 MB limit.",
        variant: "destructive",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleStartProcessing = async () => {
    const requiredFields = mappingData.filter((d) => d.req);
    const unmappedRequired = requiredFields.filter((f) => !columnMapping[f.id]);

    if (unmappedRequired.length > 0) {
      toast({
        title: "Validation Error",
        description: `Map required fields: ${unmappedRequired.map((f) => f.field).join(", ")}`,
        variant: "destructive",
      });
      return;
    }

    try {
      await saveMappingsMutation.mutateAsync({
        column_mappings: columnMapping,
      });

      toast({
        title: "Success",
        description:
          "Batch created and mappings saved successfully. Redirecting to batch details...",
      });
      if (batchId) {
        navigate(`/cx/payments/bulk-payment/details/${batchId}`);
      }
    } catch (error: any) {
      toast({
        title: "Failed to save mappings",
        description: error?.message || "An error occurred",
        variant: "destructive",
      });
    }
  };

  const handlePreviewValidate = async (values: any) => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("uploaded_file", selectedFile);
    formData.append("file_format", values.fileFormat);
    formData.append("duplicate_detection_mode", values.duplicateDetection);
    formData.append("payment_source", values.paymentSource);
    formData.append("remote_utility_id", remoteUtilityId.toString());

    try {
      const response = await uploadMutation.mutateAsync(formData);
      const newBatchId =
        response?.id || response?.result?.id || response?.batchId;
      if (newBatchId) {
        setBatchId(newBatchId);
      }
      setShowPreview(true);
      toast({
        title: "Preview & Validate",
        description: "Preview & Column Mapping loaded successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Upload Failed",
        description: error?.message || "Failed to upload file",
        variant: "destructive",
      });
    }
  };

  const handleClear = () => {
    setSelectedFile(null);
    setShowPreview(false);
    setColumnMapping({});
    setBatchId(null);
    form.reset();
  };

  const mappingColumns: TableColumn<any>[] = React.useMemo(
    () => [
      {
        key: "field",
        header: "System Field",
        render: (val, row) => {
          const isMapped = !!columnMapping[row.id];
          return (
            <div>
              <span className="text-gray-900 font-medium">{val}</span>{" "}
              {row.req && <span className="text-red-500">*</span>}
              {isMapped ? (
                <div className="text-green-600 text-xs mt-0.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block"></span>{" "}
                  Mapped
                </div>
              ) : row.req ? (
                <div className="text-amber-600 text-xs mt-0.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block"></span>{" "}
                  Required
                </div>
              ) : (
                <div className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-300 inline-block"></span>{" "}
                  Optional
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: "desc",
        header: "Description",
        render: (val) => <span className="text-gray-500">{val}</span>,
      },
      {
        key: "fileCol",
        header: "File Column",
        render: (_, row) => (
          <Select
            value={columnMapping[row.id] || "unmapped"}
            onValueChange={(val) =>
              setColumnMapping((prev) => ({
                ...prev,
                [row.id]: val === "unmapped" ? "" : val,
              }))
            }
          >
            <SelectTrigger className="w-full bg-white shadow-sm h-[38px]">
              <SelectValue placeholder="— Not mapped —" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unmapped">— Not mapped —</SelectItem>
              {fileColumns.map((col) => {
                const isSelectedByOther = Object.entries(columnMapping).some(
                  ([sysId, mappedCol]) => mappedCol === col.key && sysId !== row.id
                );

                return (
                  <SelectItem key={col.key} value={col.key} disabled={isSelectedByOther}>
                    {col.label}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        ),
      },
    ],
    [columnMapping, fileColumns],
  );

  const previewColumns: TableColumn<any>[] = React.useMemo(() => {
    return fileColumns.map((c) => {
      const fieldId = Object.keys(columnMapping).find(
        (k) => columnMapping[k] === c.key,
      );
      const fieldName = fieldId
        ? mappingData.find((d) => d.id === fieldId)?.field
        : null;
      return {
        key: c.key,
        header: fieldName ? (
          <div>
            <div className="font-semibold text-blue-600">{fieldName}</div>
            <div className="text-xs text-gray-500 font-normal">{c.label}</div>
          </div>
        ) : (
          c.label
        ),
        width: "200px",
      };
    });
  }, [columnMapping, fileColumns, mappingData]);

  const previewStats = [
    {
      id: "parsed",
      title: "Parsed Rows",
      count: previewData?.parseSummary?.totalRows || 0,
      icon: <FileText className="w-5 h-5 text-gray-500" />,
    },
    {
      id: "valid",
      title: "Valid",
      count: 0,
      icon: <CheckCircle2 className="w-5 h-5 text-green-500" />,
    },
    {
      id: "duplicates",
      title: "Duplicates Detected",
      count: 0,
      icon: <Copy className="w-5 h-5 text-amber-500" />,
    },
    {
      id: "errors",
      title: "Validation Errors",
      count: 0,
      icon: <AlertCircle className="w-5 h-5 text-red-500" />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/cx/payments")}
          />
          <div className="flex flex-col">
            <PageHeader
              title={"Bulk Payment Processing"}
              subtitle="Upload end-of-day settlement files from US banks and payment gateways — ACH (NACHA), Fedwire, Lockbox (BAI2), Check 21, Stripe, PayPal, Square, Zelle, and card interchange. The system parses, validates, matches to consumer accounts, and posts payments — with exception handling for anything that can't be auto-matched."
            />
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => downloadTemplate.mutate()}
          disabled={downloadTemplate.isPending}
        >
          {downloadTemplate.isPending ? "Downloading..." : "Download Template"}
        </Button>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">
              Upload Settlement File
            </h2>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Universal ingest: CSV, Excel, Fixed-width TXT, XML, JSON, EDI
            820/823, NACHA ACH, BAI2, MT940 SWIFT, lockbox, vendor, gateway,
            POS, and kiosk files up to 20 MB. Files up to 100,000 rows are
            processed asynchronously.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
            <div className="flex flex-col lg:col-span-3">
              {!selectedFile ? (
                <div
                  className="border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50 flex-grow flex flex-col items-center justify-center p-8 hover:bg-blue-50/50 transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                >
                  <div className="bg-blue-100 p-3 rounded-lg mb-4 text-blue-600">
                    <Upload className="h-6 w-6" />
                  </div>
                  <h3 className="text-sm font-medium text-gray-900 mb-1">
                    Drop settlement file here, or click to browse
                  </h3>
                  <p className="text-xs text-gray-500">
                    CSV · Excel · Fixed-width TXT · XML · JSON · EDI · NACHA ·
                    BAI2 · Max 20 MB
                  </p>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept={getAcceptedExtensions(selectedFormat)}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        handleFileSelect(e.target.files[0]);
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="border border-gray-200 rounded-lg bg-gray-50 p-6 flex flex-col items-center justify-center flex-grow">
                  <div className="bg-blue-100 p-4 rounded-full mb-4 text-blue-600">
                    <FileIcon className="h-8 w-8" />
                  </div>
                  <h3
                    className="text-sm font-medium text-gray-900 mb-1 max-w-full truncate px-4"
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-6">
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedFile(null)}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Remove File
                  </Button>
                </div>
              )}
            </div>

            <div className="lg:col-span-3">
              <DynamicForm
                fields={fields}
                form={form}
                onFieldChange={handleFieldChange}
                gridColumns={2}
                responsiveColumns={{ sm: 1, md: 2 }}
                config={{
                  showSubmitButton: false,
                  showResetButton: false,
                  classes: FIELD_CLASSES,
                }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-8 pt-4 border-t">
            <Button variant="outline" onClick={handleClear}>
              Clear
            </Button>
            <Button
              disabled={
                !selectedFile ||
                showPreview ||
                uploadMutation.isPending ||
                (uploadMutation as any).isLoading
              }
              onClick={form.handleSubmit(handlePreviewValidate)}
              variant="default"
            >
              <Eye className="h-4 w-4 mr-2" />
              {uploadMutation.isPending || (uploadMutation as any).isLoading
                ? "Uploading..."
                : "Preview & Validate"}
            </Button>
          </div>
        </div>
      </div>

      {showPreview && (
        <div className="bg-white border rounded-xl overflow-hidden shadow-sm mt-6">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="h-5 w-5 text-gray-500" />
                  <PageHeader
                    title="Preview & Column Mapping"
                    subtitle="Map file columns to standard SMART360 fields. Required:
                  Consumer/Account, Amount, Payment Date. First 5 rows shown
                  below."
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {previewStats.map((stat) => (
                <QuickStatsCard
                  key={stat.id}
                  title={stat.title}
                  count={stat.count}
                  icon={stat.icon}
                />
              ))}
            </div>

            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
                Standard Fields Mapping
              </h3>
              <div className="border rounded-lg bg-white overflow-hidden">
                <GenericTable
                  data={mappingData}
                  columns={mappingColumns}
                  rowKey="id"
                  loading={isPreviewLoading}
                />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
                Preview (First 5 Rows)
              </h3>
              <div className="border rounded-lg bg-white overflow-x-auto">
                <GenericTable
                  data={previewRows}
                  columns={previewColumns}
                  rowKey="id"
                  loading={isPreviewLoading}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="ghost" onClick={() => setShowPreview(false)}>
                Back
              </Button>
              <Button
                className="bg-blue-500 hover:bg-blue-600 text-white"
                onClick={handleStartProcessing}
                disabled={
                  saveMappingsMutation.isPending ||
                  (saveMappingsMutation as any).isLoading
                }
              >
                {saveMappingsMutation.isPending ||
                (saveMappingsMutation as any).isLoading
                  ? "Starting..."
                  : "Start Processing"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkPaymentCreate;
